import { Injectable, inject, signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, tap } from "rxjs";
import { environment } from "../../../environments/environment";
import { Router } from "@angular/router";
import { BranchScopeService } from "./branch-scope.service";

const BACKOFFICE_ALLOWED_ROLES = [
  "BRANCH_CASHIER",
  "BRANCH_DELIVERY",
  "BRANCH_ADMIN",
  "ADMIN",
] as const;

export const BACKOFFICE_UNAUTHORIZED_MESSAGE =
  "No está autorizado para iniciar sesión en el sistema, contacte a soporte.";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private branchScope = inject(BranchScopeService);
  private apiUrl = `${environment.apiUrl}/auth`;

  public isLoggedIn = signal<boolean>(!!localStorage.getItem("token"));

  private static readonly ROLE_LABELS: Record<string, string> = {
    ADMIN: "Admin",
    BRANCH_ADMIN: "Admin Sucursal",
    BRANCH_CASHIER: "Cajero",
    BRANCH_DELIVERY: "Entrega",
  };

  login(credentials: {
    email: string;
    password: string;
    acceptedVersionIds?: number[];
  }): Observable<any> {
    return this.http
      .post(`${this.apiUrl}/login`, { ...credentials, forBackoffice: true })
      .pipe(
        tap((response: any) => {
          if (response.success && response.data?.token) {
            this.saveSession(
              response.data.token,
              response.data.roles ?? [],
              response.data.name,
              response.data.email,
              response.data.id
            );
          }
        }),
      );
  }

  saveSession(
    token: string,
    roles: string[],
    name?: string,
    email?: string,
    userId?: number
  ): void {
    if (!this.hasStaffRoles(roles)) {
      throw new Error(BACKOFFICE_UNAUTHORIZED_MESSAGE);
    }
    localStorage.setItem("token", token);
    localStorage.setItem("roles", JSON.stringify(roles));
    if (name != null) localStorage.setItem("userName", name);
    if (email != null) localStorage.setItem("userEmail", email);
    if (userId != null) localStorage.setItem("userId", String(userId));
    this.branchScope.clear();
    this.isLoggedIn.set(true);
    this.router.navigate(["/"]);
  }

  verifyEmailOtp(email: string, code: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/verify-email-otp`, {
      email,
      code,
      forBackoffice: true,
    }).pipe(
      tap((response: any) => {
        if (response.success && response.data?.token) {
          this.saveSession(
            response.data.token,
            response.data.roles ?? [],
            response.data.name,
            response.data.email,
            response.data.id
          );
        }
      }),
    );
  }

  resendEmailOtp(email: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/resend-email-otp`, { email });
  }

  loginWithSocial(
    provider: string,
    idToken: string,
    acceptedVersionIds?: number[],
  ): Observable<any> {
    const body: Record<string, any> = {
      provider,
      idToken,
      forBackoffice: true,
    };
    if (acceptedVersionIds?.length) body["acceptedVersionIds"] = acceptedVersionIds;

    return this.http.post(`${this.apiUrl}/social`, body).pipe(
      tap((response: any) => {
        if (!response.success || !response.data?.token) throw response;
        this.saveSession(
          response.data.token,
          response.data.roles ?? [],
          response.data.name,
          response.data.email,
          response.data.id
        );
      }),
    );
  }

  logout(options?: { reason?: "unauthorized" }): void {
    localStorage.removeItem("token");
    localStorage.removeItem("roles");
    localStorage.removeItem("userName");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("userId");
    localStorage.removeItem("staffPermissions");
    this.branchScope.clear();
    this.isLoggedIn.set(false);
    this.router.navigate(
      ["/auth/login"],
      options?.reason === "unauthorized"
        ? { queryParams: { unauthorized: "1" } }
        : undefined
    );
  }

  getUserId(): number | null {
    const raw = localStorage.getItem("userId");
    const parsed = raw ? Number(raw) : NaN;
    if (Number.isFinite(parsed)) return parsed;
    const token = this.getToken();
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1] ?? ""));
      const id = Number(payload?.id);
      if (Number.isFinite(id)) {
        localStorage.setItem("userId", String(id));
        return id;
      }
    } catch {
      return null;
    }
    return null;
  }

  getDisplayName(): string {
    return localStorage.getItem("userName")?.trim() || "Usuario";
  }

  getDisplayRole(): string {
    const roles = this.getRoles();
    const first = roles[0];
    if (!first) return "";
    return (
      AuthService.ROLE_LABELS[first] ??
      first.replaceAll("_", " ").toLowerCase().replaceAll(/\b\w/g, (c) => c.toUpperCase())
    );
  }

  getToken(): string | null {
    return localStorage.getItem("token");
  }

  getRoles(): string[] {
    try {
      return JSON.parse(localStorage.getItem("roles") ?? "[]");
    } catch {
      return [];
    }
  }

  hasBackofficeAccess(): boolean {
    return this.hasStaffRoles(this.getRoles());
  }

  private hasStaffRoles(roles: string[]): boolean {
    return roles.some((role) =>
      (BACKOFFICE_ALLOWED_ROLES as readonly string[]).includes(role)
    );
  }
}
